import { useState, useEffect, useCallback, useRef } from 'react';
import { Landmark, RefreshCw, Trash2, CheckCircle, AlertCircle, Clock, Plus, Wifi } from 'lucide-react';
import api from '@/api/axios';
import { toast } from 'sonner';

/**
 * Syncfy (Paybook) — Conexión a bancos y SAT.
 *
 * Flujo:
 *  1. Pide un token de sesión a POST /syncfy/token (expira ~5 min).
 *  2. Abre el Syncfy Widget: el usuario captura ahí sus credenciales de banco
 *     o SAT (RFC + CIEC). Las credenciales NUNCA pasan por nuestro backend.
 *  3. Al evento "success", registra la credencial en
 *     POST /syncfy/credentials/register → dispara la primera descarga.
 *
 * Requiere: npm/yarn install @paybook/sync-widget  (ya está en package.json)
 */

const STATUS_STYLES = {
  ok:      'bg-green-50 border-green-200 text-green-700',
  syncing: 'bg-blue-50 border-blue-200 text-blue-700',
  twofa:   'bg-amber-50 border-amber-200 text-amber-700',
  error:   'bg-red-50 border-red-200 text-red-700',
  unknown: 'bg-gray-50 border-gray-200 text-gray-600',
};

const statusKind = (code) => {
  if (code == null) return 'unknown';
  if (code >= 200 && code < 300) return 'ok';
  if (code >= 100 && code < 200) return 'syncing';
  if (code === 410 || code === 408) return 'twofa';
  return 'error';
};

// Carga perezosa del widget: si el paquete no está instalado el resto de la
// página de Integraciones sigue funcionando.
async function loadSyncWidget() {
  const mod = await import('@paybook/sync-widget');
  try { await import('@paybook/sync-widget/dist/widget.css'); } catch { /* css opcional */ }
  return mod.default || mod.SyncWidget || mod;
}

const SyncfyIntegration = () => {
  const [status, setStatus] = useState(null);          // GET /syncfy/status
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);       // abriendo widget
  const [syncingId, setSyncingId] = useState(null);    // sync manual en curso
  const [syncStats, setSyncStats] = useState(null);    // GET /syncfy/sync-status
  const widgetRef = useRef(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/syncfy/status');
      setStatus(res.data);
      setCredentials(res.data?.credentials || []);
    } catch { /* backend sin configurar aún */ }
  }, []);

  const loadSyncStats = useCallback(async () => {
    try {
      const res = await api.get('/syncfy/sync-status');
      setSyncStats(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    loadStatus();
    loadSyncStats();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadStatus, loadSyncStats]);

  // Polling ligero del estado de sync mientras esté corriendo
  const watchSync = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      const data = await loadSyncStats();
      const done = data && data.status && data.status !== 'running';
      if (done || ticks > 60) {          // máx ~5 minutos
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data?.status === 'completed') {
          const s = data.stats || {};
          toast.success(
            `Sincronización completa: ${s.bank_new || 0} movimientos, ` +
            `${s.cfdis_new || 0} CFDIs nuevos`
          );
        } else if (data?.status === 'completed_with_errors') {
          toast.warning('Sincronización terminó con algunos errores — revisa el detalle');
        } else if (data?.status === 'error') {
          toast.error('La sincronización falló');
        }
        loadStatus();
      }
    }, 5000);
  }, [loadSyncStats, loadStatus]);

  const registerCredential = useCallback(async (credential) => {
    // El payload del evento success puede variar de forma; extraer con tolerancia
    const idCredential = credential?.id_credential
      || credential?.idCredential
      || credential?.data?.id_credential
      || (typeof credential === 'string' ? credential : null);
    if (!idCredential) {
      toast.error('El widget no devolvió id_credential — revisa la consola');
      console.error('[SYNCFY] payload success sin id_credential:', credential);
      return;
    }
    const idSite = credential?.id_site || credential?.data?.id_site || null;
    const siteName = credential?.site?.name || credential?.name
      || credential?.data?.site?.name || '';
    try {
      await api.post('/syncfy/credentials/register', {
        id_credential: idCredential,
        id_site: idSite,
        site_name: siteName,
      });
      toast.success('Conexión registrada. Descargando datos...');
      await loadStatus();
      watchSync();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error registrando la conexión');
    }
  }, [loadStatus, watchSync]);

  const openWidget = useCallback(async (entrypointCredential = null) => {
    setLoading(true);
    try {
      const tokenRes = await api.post('/syncfy/token');
      const token = tokenRes.data?.token;
      if (!token) throw new Error('Sin token');

      let SyncWidget;
      try {
        SyncWidget = await loadSyncWidget();
      } catch (e) {
        console.error('[SYNCFY] No se pudo cargar @paybook/sync-widget:', e);
        toast.error('Falta el paquete del widget. Ejecuta: yarn add @paybook/sync-widget');
        return;
      }

      const params = {
        token,
        config: {
          locale: 'es',
          // Solo instituciones de México (bancos + SAT)
          entrypoint: { country: 'MX' },
          navigation: { displayStatusInToast: true },
          // SAT con muchos CFDIs tarda — ampliar timeout (checklist oficial)
          socket: { timeout: 5 * 60 * 1000 },
        },
      };
      // Reconexión / actualización de una credencial existente
      if (entrypointCredential) {
        params.config.entrypoint = { credential: entrypointCredential };
      }

      // Cerrar instancia anterior si existiera
      if (widgetRef.current?.close) { try { widgetRef.current.close(); } catch { } }

      const widget = new SyncWidget(params);
      widgetRef.current = widget;
      widget.$on('success', (credential) => { registerCredential(credential); });
      widget.$on('error', (error) => {
        console.error('[SYNCFY] widget error:', error);
        toast.error('Hubo un problema conectando la institución');
      });
      widget.$on('closed', () => { loadStatus(); });
      widget.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error obteniendo token de Syncfy');
    } finally {
      setLoading(false);
    }
  }, [registerCredential, loadStatus]);

  const handleManualSync = async (idCredential) => {
    setSyncingId(idCredential);
    try {
      await api.post(`/syncfy/sync/${idCredential}`);
      toast.success('Sincronización iniciada (1-5 minutos)');
      watchSync();
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.warning(e.response.data?.detail || 'Espera 5 minutos entre sincronizaciones');
      } else {
        toast.error('Error al iniciar sincronización');
      }
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (idCredential, siteName) => {
    if (!window.confirm(`¿Desconectar ${siteName || 'esta institución'}? Los datos ya descargados se conservan.`)) return;
    try {
      await api.delete(`/syncfy/credentials/${idCredential}`);
      toast.success('Conexión eliminada');
      await loadStatus();
    } catch { toast.error('Error al desconectar'); }
  };

  const notConfigured = status && !status.configured;

  return (
    <div className="space-y-6">
      <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0F172A] flex items-center justify-center flex-shrink-0">
              <Landmark size={17} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A] text-base" data-testid="syncfy-title">
                Bancos y SAT — conexión automática
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Conecta tus cuentas bancarias y el SAT (CIEC). Tus credenciales van
                cifradas directo a Syncfy — nunca se guardan en TaxnFin.
              </p>
              {status?.environment === 'sandbox' && (
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium uppercase tracking-wider">
                  Modo pruebas (sandbox)
                </span>
              )}
            </div>
          </div>
          <button onClick={() => openWidget()} disabled={loading || notConfigured}
            data-testid="syncfy-connect-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#10B981] text-white text-xs font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors">
            {loading ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={13} />}
            {loading ? 'Abriendo...' : 'Conectar banco o SAT'}
          </button>
        </div>

        {notConfigured && (
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-700">
            <AlertCircle size={15} />
            Syncfy no está configurado en el servidor (falta SYNCFY_API_KEY).
          </div>
        )}

        {credentials.length === 0 && !notConfigured && (
          <div className="text-center py-8">
            <Wifi size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">
              Aún no hay instituciones conectadas. Haz clic en "Conectar banco o SAT".
            </p>
          </div>
        )}

        {credentials.length > 0 && (
          <div className="space-y-2" data-testid="syncfy-credentials-list">
            {credentials.map((cred) => {
              const kind = statusKind(cred.last_status);
              const lastPull = cred.last_pull
                ? new Date(cred.last_pull).toLocaleString('es-MX', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })
                : 'Nunca';
              return (
                <div key={cred.id_credential}
                  className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${kind === 'ok' ? 'bg-green-100' : kind === 'error' ? 'bg-red-100' : 'bg-gray-100'}`}>
                      {kind === 'ok'
                        ? <CheckCircle size={15} className="text-green-600" />
                        : kind === 'syncing'
                        ? <RefreshCw size={15} className="text-blue-500 animate-spin" />
                        : <AlertCircle size={15} className={kind === 'error' ? 'text-red-500' : 'text-gray-400'} />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0F172A] truncate">
                        {cred.site_name || 'Institución'}
                        {cred.is_sat && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-600 text-[10px] font-medium">SAT</span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-400 flex items-center gap-1">
                        <Clock size={10} /> Última sincronización: {lastPull}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {cred.last_status_label && (
                      <span className={`hidden md:inline-block px-2 py-1 rounded-md border text-[10px] font-medium ${STATUS_STYLES[kind]}`}>
                        {cred.last_status_label}
                      </span>
                    )}
                    {kind === 'twofa' && (
                      <button onClick={() => openWidget(cred.id_credential)}
                        className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors">
                        Ingresar token
                      </button>
                    )}
                    {kind === 'error' && (
                      <button onClick={() => openWidget(cred.id_credential)}
                        className="px-3 py-1.5 bg-white border border-amber-300 text-amber-600 text-xs font-medium rounded-lg hover:bg-amber-50 transition-colors">
                        Reconectar
                      </button>
                    )}
                    <button onClick={() => handleManualSync(cred.id_credential)}
                      disabled={syncingId === cred.id_credential}
                      data-testid={`syncfy-sync-${cred.id_credential}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                      <RefreshCw size={12} className={syncingId === cred.id_credential ? 'animate-spin' : ''} />
                      Sincronizar
                    </button>
                    <button onClick={() => handleDelete(cred.id_credential, cred.site_name)}
                      data-testid={`syncfy-delete-${cred.id_credential}`}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-red-200 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {syncStats?.status === 'running' && (
          <div className="mt-4 flex items-center gap-2 p-3 rounded-lg text-xs bg-blue-50 border border-blue-200 text-blue-700">
            <RefreshCw size={13} className="animate-spin" />
            Descargando datos de las instituciones conectadas...
          </div>
        )}
        {syncStats?.stats && syncStats.status !== 'running' && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Movimientos bancarios</p>
              <p className="text-xl font-bold text-[#0F172A]">{syncStats.stats.bank_new ?? 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">CFDIs nuevos</p>
              <p className="text-xl font-bold text-[#0F172A]">{syncStats.stats.cfdis_new ?? 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Última corrida</p>
              <p className="text-xs font-medium text-[#0F172A] flex items-center gap-1">
                <Clock size={11} className="text-gray-400" />
                {syncStats.updated_at ? new Date(syncStats.updated_at).toLocaleString('es-MX') : '—'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncfyIntegration;
